import styled, { css, useTheme } from "styled-components";
import Text from "common/Typography/Text";
import Radio from "common/Radio/Radio";
import { FormattedLicenseAvailablePackage } from "@shared/types";
import { hexToRGB } from "@shared/utils/misc";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

interface LicensePackageInfoProps {
  packageInfo: FormattedLicenseAvailablePackage;
  setSelectedPackage: (packageInfo?: FormattedLicenseAvailablePackage) => void;
  selectedPackage?: FormattedLicenseAvailablePackage;
}

const LicensePackageInfo = ({
  packageInfo,
  selectedPackage,
  setSelectedPackage,
}: LicensePackageInfoProps) => {
  const { isNewLook } = useTheme();
  const isSelected = selectedPackage?.key === packageInfo.key;
  const onClick = () =>
    setSelectedPackage(isSelected ? undefined : packageInfo);

  // New look: one selectable row - the plan over its duration, the count of
  // licenses as the figure on the right, the selection in brand blue.
  if (isNewLook)
    return (
      <NewLookRow
        role="radio"
        tabIndex={0}
        aria-checked={isSelected}
        $isSelected={isSelected}
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick();
          }
        }}
      >
        <Radio checked={isSelected} tabIndex={-1} aria-hidden={true} />
        <NewLookNames>
          <NewLookName>{packageInfo.subscriptionType}</NewLookName>
          <NewLookMeta>{packageInfo.subscriptionDuration} Days</NewLookMeta>
        </NewLookNames>
        <NewLookFigure>{packageInfo.numLicenses} Licenses</NewLookFigure>
      </NewLookRow>
    );

  return (
    <Wrapper
      $isSelected={selectedPackage?.key === packageInfo.key}
      onClick={onClick}
    >
      <div style={{ flex: "auto" }}>
        <Text>{packageInfo.subscriptionType}</Text>
        <Text type="secondary">{packageInfo.subscriptionDuration} Days</Text>
      </div>
      <Text>{packageInfo.numLicenses} Licenses</Text>
      <Radio checked={isSelected} style={{ marginLeft: 20 }} />
    </Wrapper>
  );
};

export default LicensePackageInfo;

const Wrapper = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  border: ${(p) =>
    p.$isSelected
      ? `1px solid ${p.theme.colors.primaryBlue}`
      : p.theme.borders.medium};
  border-radius: 8px;
  padding: 12px 16px;
  transition: border 0.25s ease-in-out;
  &:not(:last-of-type) {
    margin-bottom: 12px;
  }
  cursor: pointer;
`;
const NewLookRow = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 56px;
  padding: 8px 16px 8px 12px;
  border: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  cursor: pointer;
  transition: border-color 120ms, background-color 120ms;
  &:not(:last-of-type) {
    margin-bottom: 8px;
  }
  &:hover {
    border-color: ${(p) => getNewLookTokens(p.theme).hairlineStrong};
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 1px;
  }
  .ant-radio-wrapper {
    margin: 0;
  }
  ${(p) =>
    p.$isSelected &&
    css`
      &&,
      &&:hover {
        border-color: ${getNewLookTokens(p.theme).accent};
        background: ${hexToRGB(getNewLookTokens(p.theme).accent, 0.06)};
      }
    `}
`;
const NewLookNames = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;
const NewLookName = styled.span`
  ${newLookType.body};
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const NewLookMeta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const NewLookFigure = styled.span`
  ${newLookType.figure};
  font-size: 14px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
